import { useEffect, useState, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* ============================================================================
   TIPOS DE OPCIONES
============================================================================ */

interface CatalogoOpts {
  tipo: 'catalogo';
  pageSize?: number;
}

interface VendidosOpts {
  tipo: 'vendidos';
  pageSize?: number;
}

type Options = CatalogoOpts | VendidosOpts;

const LS_VENDIDOS_PAGE_SIZE_KEY = (empresaId: string) =>
  `bonos_vendidos_page_size_${empresaId}`;

/* ============================================================================
   TIPOS DE RETORNO
============================================================================ */

type UseBonosCatalogoResult = {
  tipo: 'catalogo';
  bonos: any[];
  loading: boolean;
  crearBono: any;
  actualizarBono: any;
  eliminarBono: any;
  comprarBono: any;
  refetch: () => void;
};

type UseBonosVendidosResult = {
  tipo: 'vendidos';
  bonos: any[];
  loading: boolean;

  filter: string;
  setFilter: (v: string) => void;

  page: number;
  setPage: (v: number) => void;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  changePageSize: (size: number) => void;

  pageData: any[];

  eliminarBonoComprado: (id: string) => Promise<boolean>;
  refetch: () => void;

  getBonoDisponible: (
    emailCliente: string,
    idServicio: string,
    idEmpresa: string
  ) => Promise<any | null>;
};

type UseBonosResult = UseBonosCatalogoResult | UseBonosVendidosResult;

/* ============================================================================
   SOBRECARGAS TIPADAS
============================================================================ */

// CATÁLOGO
export function useBonos(
  empresaId: string | null | undefined,
  opts: CatalogoOpts
): UseBonosCatalogoResult;

// VENDIDOS
export function useBonos(
  empresaId: string | null | undefined,
  opts: VendidosOpts
): UseBonosVendidosResult;

/* ============================================================================
   IMPLEMENTACIÓN GENERAL
============================================================================ */

export function useBonos(
  empresaId: string | null | undefined,
  opts: Options
): UseBonosResult {
  const { tipo, pageSize = 15 } = opts;
  const [loading, setLoading] = useState(true);

  /* --------------------------------------------------------------------------
     1. MODO CATÁLOGO
  -------------------------------------------------------------------------- */
  if (tipo === 'catalogo') {
    const [bonos, setBonos] = useState<any[]>([]);

    const fetchBonos = async () => {
      if (!empresaId) return;
      setLoading(true);

      const { data, error } = await supabase
        .from('vista_bonos')
        .select('*')
        .eq('id_empresa', empresaId)
        .order('fecha_creacion', { ascending: false });

      if (error) {
        Sentry.captureException(error, { extra: { empresaId } });
        console.error('Error cargando bonos:', error);
        setBonos([]);
      } else {
        setBonos(data || []);
      }

      setLoading(false);
    };

    const crearBono = async (bono: any) => {
      if (!empresaId) return;

      const { error } = await supabase
        .from('bonos')
        .insert([{ id_empresa: empresaId, activo: true, ...bono }]);

      if (error) {
        Sentry.captureException(error, { extra: { empresaId } });
        return { ok: false, error };
      }
      await fetchBonos();
      return { ok: true };
    };

    const actualizarBono = async (id_bono: string, data: any) => {
      const { data: updated, error } = await supabase
        .from('bonos')
        .update(data)
        .eq('id_bono', id_bono)
        .select();

      if (error) {
        Sentry.captureException(error, { extra: { id_bono } });
        return { ok: false, error };
      }

      if (!updated || updated.length === 0) {
        return { ok: false, error: 'Sin permisos para actualizar este bono' };
      }

      await fetchBonos();
      return { ok: true };
    };

    const eliminarBono = async (id_bono: string) => {
      // Verificar que el bono existe y pertenece a la empresa
      const { data: bono, error: errorBono } = await supabase
        .from('bonos')
        .select('id_bono')
        .eq('id_bono', id_bono)
        .eq('id_empresa', empresaId)
        .single();

      if (errorBono || !bono) {
        toast.error('No tienes permisos para eliminar este bono.');
        return false;
      }

      // Red de seguridad: aunque el botón se deshabilita en la UI cuando
      // tiene_ventas es true, revalidamos aquí por si la acción llega por
      // otro flujo.
      const { count, error: errorCount } = await supabase
        .from('bonos_comprados')
        .select('id_bono_comprado', { count: 'exact', head: true })
        .eq('id_bono', id_bono);

      if (errorCount) {
        Sentry.captureException(errorCount, { extra: { id_bono } });
        toast.error('Error al verificar el bono.');
        return false;
      }

      if (count && count > 0) {
        toast.error('No se puede eliminar un bono que ya ha sido vendido.');
        return false;
      }

      const { error } = await supabase
        .from('bonos')
        .delete()
        .eq('id_bono', id_bono);

      if (error) {
        Sentry.captureException(error, { extra: { id_bono } });
        toast.error('Error al eliminar el bono.');
        return false;
      }

      toast.success('Bono eliminado correctamente.');
      await fetchBonos();
      return true;
    };

    const comprarBono = async ({
      id_bono,
      id_usuario,
      usuario_creacion,
    }: {
      id_bono: string;
      id_usuario: string;
      usuario_creacion: string;
    }) => {
      if (!empresaId) return { ok: false, error: 'Empresa no válida' };

      const { error } = await supabase.from('bonos_comprados').insert([
        {
          id_bono,
          id_usuario,
          usuario_creacion,
          activo: true,
        },
      ]);

      if (error) {
        Sentry.captureException(error, { extra: { id_bono, id_usuario } });
        return { ok: false, error };
      }
      return { ok: true };
    };

    useEffect(() => {
      fetchBonos();
    }, [empresaId]);

    const result: UseBonosCatalogoResult = {
      tipo: 'catalogo',
      bonos,
      loading,
      crearBono,
      actualizarBono,
      eliminarBono,
      comprarBono,
      refetch: fetchBonos,
    };

    return result;
  }

  /* --------------------------------------------------------------------------
     2. MODO VENDIDOS
  -------------------------------------------------------------------------- */

  const [bonosVendidos, setBonosVendidos] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(() => {
    if (typeof window === 'undefined' || !empresaId) return pageSize;
    const saved = localStorage.getItem(LS_VENDIDOS_PAGE_SIZE_KEY(empresaId));
    return saved ? Number(saved) : pageSize;
  });

  const fetchVendidos = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('vista_bonos_comprados')
      .select('*')
      .eq('id_empresa', empresaId)
      .order('fecha_compra', { ascending: false });

    if (error) {
      Sentry.captureException(error, { extra: { empresaId } });
      console.error('Error cargando bonos vendidos:', error);
      setBonosVendidos([]);
    } else {
      setBonosVendidos(data || []);
    }

    setLoading(false);
  }, [empresaId]);

  useEffect(() => {
    fetchVendidos();
  }, [fetchVendidos]);

  useEffect(() => {
    if (typeof window === 'undefined' || !empresaId) return;
    localStorage.setItem(
      LS_VENDIDOS_PAGE_SIZE_KEY(empresaId),
      String(pageSizeState)
    );
  }, [pageSizeState, empresaId]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return bonosVendidos.filter((v) => {
      return (
        (v.nombre_bono || '').toLowerCase().includes(q) ||
        (v.nombre_servicio || '').toLowerCase().includes(q) ||
        (v.nombre_cliente || '').toLowerCase().includes(q) ||
        (v.email_cliente || '').toLowerCase().includes(q)
      );
    });
  }, [bonosVendidos, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSizeState));

  // Si el filtro reduce los resultados (o cambia el tamaño de página) y la
  // página actual queda fuera de rango, la traemos de vuelta al máximo
  // válido. Sin esto, pageData podía quedar vacío aunque sí hubiera
  // coincidencias, porque el slice se calculaba con una página "fantasma"
  // (p.ej. estabas en la página 2 y el filtro dejó solo 1 página de resultados).
  useEffect(() => {
    setPage((p) => (p > totalPages ? totalPages : p));
  }, [totalPages]);

  // Cada vez que cambia el texto del filtro, volvemos a la primera página.
  useEffect(() => {
    setPage(1);
  }, [filter]);

  const pageData = useMemo(() => {
    const start = (page - 1) * pageSizeState;
    return filtered.slice(start, start + pageSizeState);
  }, [filtered, page, pageSizeState]);

  const changePageSize = (size: number) => {
    setPageSizeState(size);
    setPage(1);
  };

  const eliminarBonoComprado = async (id_bono_comprado: string) => {
    // Verificar que el bono existe y pertenece a la empresa antes de eliminar
    const { data: bono, error: errorBono } = await supabase
      .from('bonos_comprados')
      .select('id_bono_comprado')
      .eq('id_bono_comprado', id_bono_comprado)
      .single();

    if (errorBono || !bono) {
      toast.error('No tienes permisos para eliminar este bono.');
      return false;
    }

    const { error } = await supabase
      .from('bonos_comprados')
      .delete()
      .eq('id_bono_comprado', id_bono_comprado);

    if (error) {
      Sentry.captureException(error, { extra: { id_bono_comprado } });
      toast.error('No se pudo eliminar el bono.');
      return false;
    }

    toast.success('Bono eliminado correctamente.');
    await fetchVendidos();
    return true;
  };

  const getBonoDisponible = useCallback(
    async (emailCliente: string, idServicio: string, idEmpresa: string) => {
      if (!emailCliente) return null;

      const { data: users, error: userErr } = await supabase
        .from('vista_clientes')
        .select('id_usuario')
        .eq('email', emailCliente)
        .limit(1);

      if (userErr) {
        Sentry.captureException(userErr, { extra: { emailCliente } });
        return null;
      }
      if (!users || users.length === 0) return null;

      const userId = users[0].id_usuario;

      const { data: bonos, error } = await supabase
        .from('vista_bonos_comprados')
        .select(
          'id_bono_comprado, id_bono, id_servicio, usos_restantes, cantidad_servicios, fecha_vencimiento'
        )
        .eq('id_usuario', userId)
        .eq('id_empresa', idEmpresa)
        .eq('activo', true)
        .gt('usos_restantes', 0)
        .gte('fecha_vencimiento', new Date().toISOString())
        .eq('id_servicio', idServicio);

      if (error) {
        Sentry.captureException(error, {
          extra: { userId, idServicio, idEmpresa },
        });
        return null;
      }
      if (!bonos || bonos.length === 0) return null;

      return bonos[0];
    },
    []
  );

  const result: UseBonosVendidosResult = {
    tipo: 'vendidos',
    bonos: bonosVendidos,
    loading,

    filter,
    setFilter,

    page,
    setPage,
    totalPages,
    pageSize: pageSizeState,
    totalItems: filtered.length,
    changePageSize,
    pageData,

    eliminarBonoComprado,
    refetch: fetchVendidos,

    getBonoDisponible,
  };

  return result;
}